import ContactsIcon from '@mui/icons-material/Contacts';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import SmsIcon from '@mui/icons-material/Sms';
import VoicemailIcon from '@mui/icons-material/Voicemail';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import {
  Button,
  FormControlLabel,
  FormGroup,
  MenuItem,
  Paper,
  Tab,
} from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import { Formik } from 'formik';
import {
  PhoneNumberFormat as PNF,
  PhoneNumberUtil,
} from 'google-libphonenumber';
import querystring from 'querystring';
import React, { useMemo, useRef, useState } from 'react';
import { useMutation } from 'react-query';
import {
  useRecoilState,
  useRecoilStateLoadable,
  useRecoilValueLoadable,
  useSetRecoilState,
} from 'recoil';
import { isPasswordValid } from 'src/common/validators';
import * as Yup from 'yup';

import Axios from '../../../common/axiosInterceptor';
import { LabelledOutline } from '../../../common/final-form-material-ui';
import {
  FormikSelect,
  FormikSwitch,
  FormikTextField,
  MaterialAutocomplete,
} from '../../../common/formik-material-ui';
import '../../../common/formik-material-ui/checkEmailAvailability';
import '../../../common/formik-material-ui/isValidNumber';
import '../../../common/formik-material-ui/isValidNumberNotRequiredIfEmpty';
import { useMakePHPRequest } from '../../../common/useMakePHPRequest';
import { trackSaveAddAgent } from '../../../common/userTracking';
import { Role, RoleLevel, user } from '../../../recoil/atoms/user';
import {
  methodNameEnum,
  refetchToggle,
} from '../../../recoil/selectors/makePHPRequest';
import { permissionsSelector } from '../../../recoil/selectors/permissionsSelector';
import { getApiMutation } from '../../../services/rest/getApiMutation';
import {
  endpointEnum,
  makeApiRequestCached,
} from '../../../services/rest/makeAPIRequest';
import { useSnackbarWithStyles } from '../../components/notifications/SnackbarProviderWithStyles';
import { POWERDIALER_MACHINE_DETECT_PRODUCT_ID } from '../../pages/ManageAccount';
import { countries } from '../countries';
import TabLabel from '../general/TabLabel';
import { PasswordStrength } from '../PasswordStrength/PasswordStrength';
import { getPowerlistDialerProductMap, products } from '../products';

import { AddNumberModalTab } from './AddNumberModalTab';
import { AddSMSTemplateTab } from './AddSMSTemplateTab';
import { AddVMDropModalTab } from './AddVMDropModalTab';
import { getErrorTabIndex, isErrorInTab } from './DevicesAndRoutingModal';
import ProductChangesConfirmModal from './ProductChangesConfirmModal';
import { WizardModal } from './WizardModal';

// Modal used to add a new agent Manage > Agent > Add
export function AddNewAgentModal({ show, handleClose, handleShow }) {
  const [tabIndex, setTabIndex] = useState(0);
  const handleChange = (formik) => async (event, newIndex) => {
    setTabIndex(+newIndex);
  };
  const [combineFirstLastName, setCombineFirstLastName] = useState(true);
  const [flag, setFlag] = useState(false);
  const previousRef = useRef();
  const [userAtom, setUserAtom] = useRecoilStateLoadable(user);
  const mutation = useMutation(getApiMutation(userAtom.contents, setUserAtom));
  const [hideBuyNumber, setHideBuyNumber] = useState(false);
  const [passwordFocus, setPasswordFocus] = useState(false);
  const [showProductChanges, setShowProductChanges] = useState(false);
  const [userProductChanges, setUserProductChanges] = useState(false);
  const [productChangesLoading, setProductChangesLoading] = useState(false);
  const [values, setValues] = useState({});
  const [actions, setActions] = useState({});
  const permissionsLoadable = useRecoilValueLoadable(permissionsSelector);
  const {
    role,
    enableFreeBillingTier,
    canAccessPowerlistProduct,
    isMachineDetectionFeatureEnabled,
    inTrial,
  } = permissionsLoadable.contents;

  const useStyles = makeStyles((theme) => ({
    field: {
      marginTop: theme.spacing(1), // space between fields
      marginBottom: theme.spacing(1), // space between fields
      // color: 'purple',
      // '&::placeholder': {
      //   color: 'black',
      //   opacity: '0.6',
      // },
    },
    add: {
      color: theme.palette.primary.contrastText,
      backgroundColor: theme.palette.primary.main,
      marginLeft: theme.spacing(1), // space between fields
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
    },
    button: {
      marginLeft: theme.spacing(1), // space between fields
    },
    selectNumberButton: {
      '&:hover': {
        backgroundColor: 'red',
      },
    },
    tab: {
      maxWidth: 'none',
      borderBottom: `1px solid ${theme.palette.divider}`,
      '& .MuiTab-wrapper': {
        flexDirection: 'row',
      },
      '&[tabindex="0"]': {
        backgroundColor: theme.palette.divider,
      },
    },
    tabs: {},
    header: {
      color: 'var(--primary)', // pull color from primary
      fontWeight: 'bold',
      textTransform: 'uppercase',
      marginBottom: 10,
    },
    modal: {
      paddingBottom: 0,
      maxHeight: 'calc(100vh - 210px)',
      overflowY: 'auto',
    },
    defaultAction: {
      marginTop: theme.spacing(2),
    },
    spinner: {
      position: 'fixed !important',
    },
    selectMenuPaper: {
      maxHeight: 460, //'calc(100% - 96px)'
    },
    outbound: {
      marginTop: theme.spacing(1), // space between fields
      marginBottom: theme.spacing(1), // space between fields
      flexGrow: 1,
      width: '100px',
    },
    billing: {
      marginTop: theme.spacing(1), // space between fields
      marginBottom: theme.spacing(1), // space between fields
      flexGrow: 1,
      width: '70px',
    },
    editButton: {
      maxWidth: '155px',
      maxHeight: '50px',
      minWidth: '155px',
      minHeight: '50px',
    },
    fieldRow: {
      display: 'flex',
      marginLeft: '2px',
      marginRight: '2px',
    },
    password: {
      '& input': {
        WebkitTextSecurity: 'disc',
      },
    },
  }));
  const setRefetchToggleForAgents = useSetRecoilState(
    refetchToggle(endpointEnum.GET_BUSINESS_USERS)
  );
  const refetchToggleForBusinessDevices = useSetRecoilState(
    refetchToggle(endpointEnum.GET_BUSINESS_DEVICES)
  );
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();

  // Add Number Modal Code
  const [numberToBuy, setNumberToBuy] = useState(0);
  const [rate, setRate] = useState(0);
  const makeRequest = useMakePHPRequest({
    userAtom: userAtom.contents,
  });

  const setRefetchToggleForNumbers = useSetRecoilState(
    refetchToggle(endpointEnum.GET_BUSINESS_NUMBERS)
  );

  // VM Drop Modal Code
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [filenames, setFilenames] = useState({
    voicemail: '',
  });
  const [filepaths, setFilepaths] = useState({
    voicemail: '',
  });
  const setRefetchToggleForVmDrop = useSetRecoilState(
    refetchToggle(endpointEnum.GET_VM_DROP)
  );
  const [vmDropRecording, setVmDropRecording] = useState(false);

  // SMS Template Code
  const setRefetchToggleForSmsTemplates = useSetRecoilState(
    refetchToggle(endpointEnum.GET_SMS_TEMPLATE)
  );

  const [businessNumbersCached, businessNumbersLoadable] = makeApiRequestCached(
    {
      endpoint: endpointEnum.GET_BUSINESS_NUMBERS,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_NUMBERS,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result
        ?.filter(
          (item) =>
            item.type.toLowerCase() === 'queue' ||
            item.type.toLowerCase() === 'ivr' ||
            item.type.toLowerCase() === 'group' ||
            item.type.toLowerCase() === 'unallocated'
          // || item.userid == id
        )
        .reduce(
          (acc, item) => ({
            ...acc,
            myNumbers:
              // item.userid == id &&
              item.sms ? acc.myNumbers.concat(item.DID) : acc.myNumbers,
            allNumbers: acc.allNumbers.concat(item.DID),
            lookupNumbers: {
              ...acc.lookupNumbers,
              [item.DID]: item,
            },
          }),
          {
            myNumbers: [],
            allNumbers: [],
            lookupNumbers: {},
          }
        ) ?? cached.current,
    {
      myNumbers: [],
      allNumbers: [],
      lookupNumbers: {},
    },
    []
  );

  const businessNumbersLoadableFiltered = businessNumbersLoadable?.contents?.response?.data?.result
    ?.filter(
      (item) =>
        item.type.toLowerCase() === 'queue' ||
        item.type.toLowerCase() === 'ivr' ||
        item.type.toLowerCase() === 'group' ||
        item.type.toLowerCase() === 'unallocated'
      // || item.userid == id
    )
    .reduce(
      (acc, item) => ({
        ...acc,
        myNumbers:
          // item.userid == id &&
          item.sms ? acc.myNumbers.concat(item.DID) : acc.myNumbers,
        allNumbers: acc.allNumbers.concat(item.DID),
        lookupNumbers: {
          ...acc.lookupNumbers,
          [item.DID]: item,
        },
      }),
      {
        myNumbers: [],
        allNumbers: [],
        lookupNumbers: {},
      }
    )?.allNumbers;

  const callerIdNumberValidation =
    numberToBuy === 0
      ? Yup.string()
          .isValidE164Number('Must be valid number for countrycode')
          .required('Required')
      : Yup.string()
          .notRequired()
          .isValidNumberNotRequiredIfEmpty(
            'Must be valid number for countrycode'
          );

  const validationSchema = React.useMemo(
    () =>
      Yup.object().shape({
        addAgent: Yup.object({
          fname: Yup.string().required('Required'),
          lname: Yup.string().required('Required'),
          email: Yup.string()
            .required('Required')
            .email('Must be a valid Email Address')
            .checkEmailAvailability(
              'This email address is already in use',
              previousRef,
              mutation
            ),
          password: Yup.string()
            .matches(
              /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*\W).{8,}$/,
              'Required'
            )
            .test({
              name: 'noBannedWords',
              message: 'No banned words allowed',
              test: function (password) {
                if (
                  this.parent.email &&
                  !this.parent.email.includes('kixie.com')
                ) {
                  return true;
                }

                if (password) {
                  const { valid } = isPasswordValid(password);
                  return valid;
                }

                return false;
              },
            })
            .required('Required'),
          outboundCallerId: Yup.string()
            .required('Required')
            .max(60, 'Maximum 60 characters'),
          calleridNumber: callerIdNumberValidation,
        }),
        buyNumber: Yup.object({
          numberName: Yup.string().max(
            100,
            'Name must be a maximum of 100 characters'
          ),
        }),
        VMDrop: vmDropRecording
          ? Yup.object().shape({
              VMDropName: Yup.string().max(100, 'Maximum 100 characters'),
            })
          : filenames.voicemail
          ? Yup.object().shape(
              {
                VMDropName: Yup.string().required('Required'),
                mp3Name: Yup.string()
                  .required('Required')
                  .checkFileSize(20_000_000, 'File must be at or below 20mb'),
              },
              [['VMDropName', 'mp3Name']]
            )
          : Yup.object().shape(
              {
                VMDropName: Yup.string(),
                mp3Name: Yup.string()
                  .ensure()
                  .when('VMDropName', {
                    is: (val) => val !== undefined,
                    then: Yup.string()
                      .required('Required')
                      .checkFileSize(
                        20_000_000,
                        'File must be at or below 20mb'
                      ),
                  }),
              },
              [['VMDropName', 'mp3Name']]
            ),
        SMSTemplate: Yup.object().shape(
          {
            name: Yup.string()
              .ensure()
              .when('content', {
                is: (val) => val !== '',
                then: Yup.string()
                  .required('Required')
                  .max(100, 'Maximum 100 characters'),
              }),
            content: Yup.string()
              .ensure()
              .when('name', {
                is: (val) => val !== '',
                then: Yup.string()
                  .required('Required')
                  .max(512, 'Maximum 512 characters'),
              }),
          },
          [['name', 'content']]
        ),
      }),
    [
      filenames,
      vmDropRecording,
      businessNumbersCached,
      numberToBuy,
      businessNumbersLoadableFiltered,
    ]
  );
  const classes = useStyles();
  const [storageUser] = useRecoilState(user);

  const tiers = [
    {
      name: 'Professional',
      code: 1,
    },
    {
      name: 'Integrated',
      code: 2,
    },
    {
      disabled: role !== Role.SUPERADMIN,
      name: 'Basic',
      code: 3,
    },
  ];
  // ENG-5254: Hide "Free" Billing Tier
  if (enableFreeBillingTier) {
    tiers.push({
      disabled: role !== Role.SUPERADMIN,
      name: 'Free',
      code: 4,
    });
  }
  const minutes = [
    {
      code: 0,
      name: 'Per Minute',
    },
    {
      code: 1,
      name: 'Unlimited',
    },
  ];

  const roleDropdown = useMemo(
    () =>
      // iterate over values in dropdown and determine whether each role will be shown/hide and selectable/unselectable.
      Object.keys(RoleLevel)
        .map((roleName) => ({
          role: roleName, // role name: eg. superadmin, admin, manager, user
          disabled: RoleLevel[role] > RoleLevel[roleName], // disable if viewer role has lower privilege than viewed user (0 === superadmin, 30 = user)
          hide:
            roleName === Role.SUPERADMIN &&
            userAtom?.contents?.businessid !== 995 &&
            userAtom?.contents?.businessid !== 21594, // hide superadmin unless business is 995 or 21594
        }))
        .reverse(),
    [role]
  );

  const showPasswordStrength = (focused, error) => {
    return focused && error;
  };

  const tabNames = useMemo(
    () =>
      hideBuyNumber === false
        ? [
            {
              title: 'Add Agent',
              icon: SettingsApplicationsIcon,
            },
            {
              title: 'Buy Number',
              icon: ContactsIcon,
            },
            {
              title: 'VM Drop',
              icon: VoicemailIcon,
            },
            {
              title: 'SMS Template',
              icon: SmsIcon,
            },
          ]
        : [
            {
              title: 'Add Agent',
              icon: SettingsApplicationsIcon,
            },
            {
              title: 'VM Drop',
              icon: VoicemailIcon,
            },
            {
              title: 'SMS Template',
              icon: SmsIcon,
            },
          ],
    [hideBuyNumber]
  );

  const tabToFields = useMemo(
    () =>
      hideBuyNumber === false
        ? [['addAgent'], ['buyNumber'], ['VMDrop'], ['SMSTemplate']]
        : [['addAgent'], ['VMDrop'], ['SMSTemplate']],
    [hideBuyNumber]
  );

  const submitForm = async (values, actions) => {
    const usertype =
      values.addAgent.userType === 0
        ? 'user'
        : values.addAgent.userType === 1
        ? 'manager'
        : values.addAgent.userType === 2
        ? 'admin'
        : values.addAgent.userType === 3
        ? 'superadmin'
        : 'user';

    const phoneUtil = PhoneNumberUtil.getInstance();
    const number = values.addAgent.calleridNumber
      ? phoneUtil.parseAndKeepRawInput(
          values.addAgent.calleridNumber,
          values.addAgent.countrycode
        )
      : null;

    const url = `${process.env.VITE_APP_PHP_DOMAIN}/rest/functions.php`;
    const forwardNumber =
      businessNumbersLoadableFiltered?.length && values.calleridNumber
        ? values.addAgent.calleridNumber
        : businessNumbersLoadableFiltered?.length && numberToBuy === 0
        ? values.addAgent.calleridNumber
        : businessNumbersLoadableFiltered?.length &&
          numberToBuy !== 0 &&
          values.addAgent.calleridNumber === ''
        ? numberToBuy
        : businessNumbersLoadableFiltered?.length && numberToBuy !== 0
        ? values.addAgent.calleridNumber
        : !businessNumbersLoadableFiltered?.length && numberToBuy !== 0
        ? numberToBuy
        : phoneUtil.format(number, PNF.E164);

    const dataObj = {
      call: 'submitNewUser2',
      fname: values.addAgent.fname,
      lname: values.addAgent.lname,
      email: values.addAgent.email,
      tier: values.addAgent.billingTier,
      minutes: values.addAgent.minutesPlan,
      countrycode: values.addAgent.countrycode,
      usertype,
      calleridName: values.addAgent.outboundCallerId,
      forwardNumber,
      businessid: storageUser?.businessid,
      apiKey: storageUser?.apikey,
      purchasedNumberNewAgent: forwardNumber === numberToBuy,
    };
    if (values.addAgent?.password && values.addAgent.password !== '') {
      dataObj.password = values.addAgent.password;
    }
    const formData = querystring.stringify(dataObj);

    const newAgentResult = await Axios.post(url, formData, {
      withCredentials: true,
    });
    if (typeof newAgentResult.data === 'string') {
      const split = newAgentResult.data.split('1');
      if (JSON.parse(split[1]).meta.success === false) {
        enqueueSnackbarWithStyles({
          message: JSON.parse(split[1]).meta.error.replace('@', 'at'),
          alertProps: { severity: 'error' },
        });
      }
    }
    if (newAgentResult.data.meta.success) {
      enqueueSnackbarWithStyles({
        message: 'Successfully Added New Agent !',
        alertProps: { severity: 'success' },
      });
    }

    if (values.addAgent.powerlistProduct !== 0) {
      await mutation.mutateAsync({
        // placeholder for actual call
        endpoint: endpointEnum.UPDATE_POWERLIST_PRODUCT,
        payload: {
          call: 'updatePowerlistProduct',
          productValue: 1,
          productid: values.addAgent?.powerlistProduct,
          userid: newAgentResult?.data?.result?.userid,
        },
      });
    }

    if (numberToBuy) {
      const { response } = await makeRequest({
        methodName: methodNameEnum.BUY_PHONE_NUMBER,
        payload: {
          targetid: newAgentResult?.data?.result?.userid,
          destinationType: 'user',
          number: numberToBuy,
          country: values.buyNumber.country,
          rate,
          numType:
            values.buyNumber.numType.toLocaleLowerCase() === 'local'
              ? 'fixed'
              : values.buyNumber.numType.toLocaleLowerCase(),
          numberName: values.buyNumber.numberName,
          role: userAtom?.contents?.user?.role,
        },
      });
      if (response?.data?.success === 'true') {
        enqueueSnackbarWithStyles({
          message: `Successfully Purchased the Number - ${numberToBuy}`,
          alertProps: { severity: 'success' },
        });
      } else {
        enqueueSnackbarWithStyles({
          message: response?.data?.message
            ? response.data.message
            : 'Encountered An Error while trying to purchase Number',
          alertProps: { severity: 'warning' },
        });
      }
      setRefetchToggleForNumbers({});
    }

    // ENG-3501 undoing change as per CS
    // if (values.addAgent.billingTier.toString() !== '1') {
    //   values.addAgent.localPresence = false;
    // }

    if (values.addAgent.localPresence) {
      await mutation.mutateAsync({
        endpoint: endpointEnum.UPDATE_LOCAL_PRESENCE,
        payload: {
          call: methodNameEnum.UPDATE_LOCAL_PRESENCE,
          value: '1',
          userid: newAgentResult?.data?.result?.userid,
        },
      });
    }

    if (values.VMDrop.VMDropName) {
      if (values.VMDrop.mp3Type === 'record') {
        const data = new FormData();
        data.append('webm', uploadedFiles.voicemail[0]);
        data.append('businessid', storageUser.businessid);
        data.append('apikey', storageUser.apikey);
        data.append('userid', newAgentResult?.data?.result?.userid);
        data.append('call', 'HandleAudioRecording');
        data.append('id', newAgentResult?.data?.result?.userid);
        data.append('iid', 0);
        data.append('name', values.VMDrop.VMDropName);
        data.append('type', 'new-agent-voicemail-drop');
        const url = `${process.env.VITE_APP_PHP_DOMAIN}/rest/functions.php`;
        await Axios.post(url, data, { withCredentials: true });
      }
      if (values.VMDrop.mp3Type === 'mp3' && uploadedFiles.voicemail) {
        const data = new FormData();
        data.append('call', 'submitVMDropMp3File');
        data.append('businessid', storageUser.businessid);
        data.append('apikey', userAtom.contents.apikey);
        data.append('userid', newAgentResult?.data?.result?.userid);
        data.append('vmdropName', values.VMDrop.VMDropName);
        data.append('mp3', uploadedFiles.voicemail[0]);
        const url = `${process.env.VITE_APP_PHP_DOMAIN}/rest/functions.php`;
        await Axios.post(url, data, { withCredentials: true });
      }
      enqueueSnackbarWithStyles({
        message: `Successfully Added ${values.VMDrop.VMDropName} VM Drop`,
        alertProps: { severity: 'success' },
      });
      setRefetchToggleForVmDrop({});
    }

    if (values.SMSTemplate.name) {
      const { name, content } = values.SMSTemplate;
      const data = {
        call: methodNameEnum.SUBMIT_NEW_TEMPLATE,
        // apikey: userAtomContents?.apikey,
        // businessid: storageUser?.businessid,
        name,
        content,
        userid: newAgentResult?.data?.result?.userid,
      };
      const response = await mutation.mutateAsync({
        endpoint: endpointEnum.SUBMIT_NEW_TEMPLATE,
        payload: data,
      });
      setRefetchToggleForSmsTemplates({});
      if (response?.data?.success) {
        enqueueSnackbarWithStyles({
          message: 'Successfully Added SMS Template',
          alertProps: { severity: 'success' },
        });
      } else {
        enqueueSnackbarWithStyles({
          message: 'Encountered an error adding the SMS Template',
          alertProps: { severity: 'error' },
        });
      }
    }

    if (flag) {
      setFlag(false);
      setHideBuyNumber(false);
      actions.resetForm({});
      actions.setTouched({});
      handleClose();
    } else {
      actions.resetForm({});
      actions.setTouched({});
      actions.setFieldValue(
        'addAgent.billingTier',
        values.addAgent.billingTier
      );
      actions.setFieldValue(
        'addAgent.minutesPlan',
        values.addAgent.minutesPlan
      );
      setHideBuyNumber(values?.addAgent?.billingTier?.toString() === '4');
    }
    setRefetchToggleForAgents({});
    refetchToggleForBusinessDevices({});
    setProductChangesLoading(false);
  };

  return (
    <Paper>
      <Formik
        enableReinitialize={true}
        initialValues={{
          addAgent: {
            fname: '',
            lname: '',
            email: '',
            password: '',
            countrycode: 'US',
            calleridNumber: '',
            cell: '',
            billingTier: '1',
            minutesPlan: '0',
            outboundCallerId: '',
            userType: 0,
            localPresence: false,
            powerlistProduct: 0,
          },
          buyNumber: {
            numberFor: 'None',
            agent: '',
            conference: '',
            group: '',
            ivr: '',
            queue: '',
            country: 'US',
            areaCode: '',
            smsAble: 'Y',
            numType: 'Local',
            availableNumbers: [],
            numberName: '',
          },
          VMDrop: {
            VMDropName: '',
            mp3Type: 'mp3',
            mp3File: '',
            mp3Name: '',
          },
          SMSTemplate: {
            name: '',
            content: '',
          },
        }}
        // initialValues={ivs}
        validationSchema={validationSchema}
        onSubmit={async (values, actions) => {
          setValues(values);
          setActions(actions);
          const minutesPlan = Number(values.addAgent.minutesPlan);
          const billingTier = Number(values.addAgent.billingTier);
          let productsToUpdate = [];
          let tierName = '';
          if (minutesPlan === 1) {
            if (billingTier === 1) {
              tierName = 'Enterprise Unlimited';
            } else if (billingTier === 2) {
              tierName = 'Integrated Unlimited';
            } else if (billingTier === 3) {
              tierName = 'Basic Unlimited';
            }
          } else if (minutesPlan === 0) {
            if (billingTier === 1) {
              tierName = 'Enterprise';
            } else if (billingTier === 2) {
              tierName = 'Integrated';
            } else if (billingTier === 3) {
              tierName = 'Basic';
            }
          }
          if (billingTier === 4) {
            tierName = 'payperuse';
          }
          const productName = products.find(
            (product) => product?.tier === tierName
          )?.product;
          productsToUpdate = [
            ...productsToUpdate,
            {
              userId: 0,
              product: productName,
            },
          ];
          if (values.addAgent.powerlistProduct !== 0) {
            const powerlistDialerProductMap = getPowerlistDialerProductMap(
              POWERDIALER_MACHINE_DETECT_PRODUCT_ID
            );
            const selectedPowerlistProduct = powerlistDialerProductMap?.find(
              (el) => el?.id === values.addAgent.powerlistProduct
            );
            productsToUpdate = [
              ...productsToUpdate,
              {
                userId: 0,
                product: selectedPowerlistProduct?.product,
              },
            ];
          }
          if (values.addAgent.localPresence) {
            productsToUpdate = [
              ...productsToUpdate,
              {
                userId: 0,
                product: 'localpresencelite',
              },
            ];
          }
          setUserProductChanges(productsToUpdate);
          !inTrial ? setShowProductChanges(true) : submitForm(values, actions);
        }}
      >
        {(formik) => {
          return (
            <TabContext value={String(tabIndex)}>
              <WizardModal
                className="addNewAgentModal"
                title={tabNames[tabIndex]?.title}
                isLoading={formik.isSubmitting || productChangesLoading}
                navigation={
                  <>
                    <Tab
                      label="Add New Agent"
                      disabled={true}
                      className={clsx(classes.tab)}
                    />
                    <TabList
                      onChange={handleChange(formik)}
                      aria-label="Add New Agent"
                      orientation="vertical"
                      variant="standard"
                      className={clsx(classes.tabs, {})}
                    >
                      {tabNames.map((tabItem, index) => (
                        <Tab
                          {...{ name: tabItem.title }} // name not found in Tab
                          key={index}
                          className={classes.tab}
                          label={
                            <TabLabel
                              index={index}
                              formik={formik}
                              tabToFields={tabToFields}
                              tabItem={tabItem}
                            />
                          }
                          value={String(index)}
                        />
                      ))}
                    </TabList>
                  </>
                }
                dialogActions={
                  <>
                    <Button
                      className={classes.button}
                      color="inherit"
                      variant="contained"
                      disabled={formik.isSubmitting || productChangesLoading}
                      onClick={() => {
                        handleClose();
                        formik.setTouched({});
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      className={classes.button}
                      variant="contained"
                      color="primary"
                      disabled={
                        formik.isSubmitting ||
                        !formik.dirty ||
                        productChangesLoading
                      }
                      onClick={(event) => {
                        setFlag(true);
                        // // if async function then save event since it will be garbage collected after any await
                        // const savedEvent = { ...event };

                        // trigger show errors if errors exists
                        formik.handleSubmit(event);

                        // switch tabs and suppress submit if error not in current tab (submit will block if errors detected)
                        if (
                          !formik.isValid && // if form invalid
                          !isErrorInTab(tabIndex, formik.errors, tabToFields) // and error not in this tab
                        ) {
                          const errorTabIndex = getErrorTabIndex(
                            formik.errors,
                            tabToFields
                          );
                          /**
                           * switch to tab containing error
                           *
                           * since form validation is in flight and makes page rendering updates
                           * set state change in separate event loop cycle (tick) since it also makes page rendering updates
                           * putting state changes in a separate tick will prevent coliding page rendering updates
                           *
                           * @see https://blog.sessionstack.com/how-javascript-works-event-loop-and-the-rise-of-async-programming-5-ways-to-better-coding-with-2f077c4438b5
                           */
                          setTimeout(() => setTabIndex(errorTabIndex ?? 0));
                        }
                      }}
                    >
                      ADD AGENT
                    </Button>
                  </>
                }
                size="lg"
                show={show}
                onHide={() => {
                  setHideBuyNumber(false);
                  handleClose();
                  formik.setTouched({});
                }}
                onSubmit={() => {
                  trackSaveAddAgent();
                  formik.handleSubmit();
                }}
              >
                <TabPanel value="0">
                  <FormGroup>
                    <form>
                      <div className="container">
                        <LabelledOutline label="User Settings">
                          <div className="row">
                            <div className="col">
                              <FormikTextField
                                name="addAgent.fname"
                                className={classes.field}
                                formik={formik}
                                // autoFocus={true}
                                fullWidth
                                label="First Name"
                                inputProps={{ maxLength: 45 }}
                                size="small"
                                required
                                variant="outlined"
                                onKeyUp={(e) => {
                                  if (combineFirstLastName) {
                                    formik.setFieldValue(
                                      'addAgent.outboundCallerId',
                                      e.target.value +
                                        ' ' +
                                        formik.values.addAgent.lname
                                    );
                                  }
                                }}
                              />
                            </div>
                            <div className="col">
                              <FormikTextField
                                name="addAgent.lname"
                                className={classes.field}
                                formik={formik}
                                fullWidth
                                label="Last Name"
                                inputProps={{ maxLength: 45 }}
                                size="small"
                                required
                                variant="outlined"
                                onKeyUp={(e) => {
                                  if (combineFirstLastName) {
                                    formik.setFieldValue(
                                      'addAgent.outboundCallerId',
                                      formik.values.addAgent.fname +
                                        ' ' +
                                        e.target.value
                                    );
                                  }
                                }}
                              />
                            </div>
                            <div className="w-100"></div>
                            <div className="col">
                              <FormikTextField
                                name="addAgent.email"
                                className={classes.field}
                                formik={formik}
                                fullWidth
                                style={{ color: 'purple' }}
                                label="Email"
                                size="small"
                                type="email"
                                required
                                variant="outlined"
                                autoComplete="new-email"
                              />
                            </div>
                            <div className="col">
                              <FormikTextField
                                name="addAgent.outboundCallerId"
                                className={classes.field}
                                formik={formik}
                                fullWidth
                                label="Outbound Caller ID Name"
                                size="small"
                                required
                                inputProps={{ maxLength: 60 }}
                                variant="outlined"
                                onFocus={(e) => setCombineFirstLastName(false)}
                              />
                            </div>

                            <div className="w-100"></div>
                            <div className="col">
                              <FormikSelect
                                name="addAgent.countrycode"
                                className={classes.field}
                                formik={formik}
                                fullWidth
                                label="Country"
                                size="small"
                                variant="outlined"
                              >
                                {countries.map((country, index) => (
                                  <MenuItem key={index} value={country.code}>
                                    {country.name}
                                  </MenuItem>
                                ))}
                              </FormikSelect>
                            </div>
                            <div className="col">
                              {businessNumbersCached.current.allNumbers
                                .length ? (
                                <MaterialAutocomplete
                                  type="text"
                                  name="addAgent.calleridNumber"
                                  fullWidth
                                  formik={formik}
                                  defaultValue={null}
                                  label="Outbound Caller ID Number"
                                  size="small"
                                  variant="outlined"
                                  multiple={false}
                                  options={
                                    businessNumbersCached.current.allNumbers
                                  }
                                  freeSolo={true}
                                  onKeyUp={(e) => {
                                    formik.setFieldValue(
                                      'addAgent.calleridNumber',
                                      e.target.value
                                    );
                                  }}
                                  onBlur={(e) => {
                                    formik.setFieldValue(
                                      'addAgent.calleridNumber',
                                      e.target.value
                                    );
                                    formik.setFieldTouched(
                                      'addAgent.calleridNumber',
                                      true
                                    );
                                    formik.validateField(
                                      'addAgent.calleridNumber'
                                    );
                                  }}
                                  renderOption={(props, option) => {
                                    const lookup =
                                      businessNumbersCached.current
                                        ?.lookupNumbers[option];
                                    return (
                                      <li {...props}>
                                        {`${option} ${lookup?.type}: ${lookup?.destinationName}`}
                                      </li>
                                    );
                                  }}
                                  noOptionsText="No options - Click the Buy Number tab to add a number for this agent"
                                />
                              ) : (
                                <FormikTextField
                                  name="addAgent.calleridNumber"
                                  className={classes.field}
                                  formik={formik}
                                  fullWidth
                                  label="Cell or Direct #"
                                  size="small"
                                  // required
                                  variant="outlined"
                                />
                              )}
                            </div>
                            {/* Start of new group */}
                            <div className="w-100"></div>
                            <div className="col">
                              <FormikSelect
                                name="addAgent.userType"
                                className={clsx(classes.field)}
                                fullWidth
                                formik={formik}
                                label="User Type"
                                size="small"
                                variant="outlined"
                              >
                                {roleDropdown
                                  .filter((dropdown) => !dropdown.hide)
                                  .map((m, index) => (
                                    <MenuItem
                                      key={index}
                                      value={index}
                                      disabled={m.disabled}
                                    >
                                      <span
                                        style={{ textTransform: 'capitalize' }}
                                      >
                                        {m.role}
                                      </span>
                                    </MenuItem>
                                  ))}
                              </FormikSelect>
                            </div>
                            <div className="col">
                              {role &&
                                (role === Role.SUPERADMIN ||
                                  role === Role.ADMIN ||
                                  role === Role.MANAGER) && (
                                  <>
                                    <FormikTextField
                                      name="addAgent.password"
                                      className={`${classes.field} ${classes.password}`}
                                      formik={formik}
                                      fullWidth
                                      label="Password"
                                      type="input"
                                      size="small"
                                      variant="outlined"
                                      onFocus={() => {
                                        setPasswordFocus(true);
                                      }}
                                    />
                                    {showPasswordStrength(
                                      passwordFocus,
                                      formik.errors.addAgent?.password
                                    ) && (
                                      <PasswordStrength
                                        password={
                                          formik.values.addAgent?.password
                                        }
                                        email={formik.values.addAgent?.email.toLowerCase()}
                                      />
                                    )}
                                  </>
                                )}
                            </div>
                          </div>
                        </LabelledOutline>
                      </div>
                      <div className="container">
                        <LabelledOutline label="User Products">
                          <div className="row">
                            <div className="w-100"></div>
                            <div className="w-100"></div>
                            <div className="col">
                              <FormikSelect
                                name="addAgent.billingTier"
                                className={classes.field}
                                formik={formik}
                                fullWidth
                                label="Billing Tier"
                                size="small"
                                variant="outlined"
                                onChange={(event) => {
                                  if (event.target.value != 1) {
                                    formik.values.addAgent.localPresence = false;
                                    formik.setFieldValue(
                                      'addAgent.powerlistProduct',
                                      0
                                    );
                                  }
                                  setHideBuyNumber(event.target.value === 4);
                                  if (event.target.value === 4) {
                                    formik.setFieldValue(
                                      'addAgent.powerlistProduct',
                                      0
                                    );
                                    formik.setFieldValue(
                                      'addAgent.localPresence',
                                      0
                                    );
                                  }
                                }}
                              >
                                {tiers.map((t, index) => (
                                  <MenuItem
                                    disabled={t.disabled}
                                    key={index}
                                    value={t.code}
                                  >
                                    {t.name}
                                  </MenuItem>
                                ))}
                              </FormikSelect>
                            </div>
                            <div className="col">
                              <FormikSelect
                                name="addAgent.minutesPlan"
                                className={classes.field}
                                formik={formik}
                                fullWidth
                                label="Minutes Plan"
                                size="small"
                                variant="outlined"
                                disabled={
                                  formik.values.addAgent.billingTier.toString() ===
                                  '4'
                                }
                              >
                                {minutes.map((m, index) => (
                                  <MenuItem key={index} value={m.code}>
                                    {m.name}
                                  </MenuItem>
                                ))}
                              </FormikSelect>
                            </div>
                            {/* Start of new group */}
                            <div className="w-100"></div>
                            {canAccessPowerlistProduct && (
                              <div className="col">
                                <FormikSelect
                                  name="addAgent.powerlistProduct"
                                  className={classes.field}
                                  formik={formik}
                                  fullWidth
                                  label="PowerDialer Product"
                                  size="small"
                                  variant="outlined"
                                  required
                                  disabled={
                                    formik.values.addAgent.billingTier.toString() !==
                                    '1'
                                  }
                                  value={
                                    formik.values.addAgent.billingTier.toString() !==
                                    '1'
                                      ? 0
                                      : formik.values.addAgent.powerlistProduct
                                  }
                                >
                                  <MenuItem value={0}>None</MenuItem>
                                  <MenuItem value={30}>
                                    PowerDialer One
                                  </MenuItem>
                                  <MenuItem value={31}>
                                    PowerDialer Four
                                  </MenuItem>
                                  <MenuItem value={32}>
                                    PowerDialer Ten
                                  </MenuItem>
                                  {isMachineDetectionFeatureEnabled && (
                                    <MenuItem
                                      value={
                                        POWERDIALER_MACHINE_DETECT_PRODUCT_ID
                                      }
                                    >
                                      PowerDialer AI Human Detect
                                    </MenuItem>
                                  )}
                                </FormikSelect>
                              </div>
                            )}
                            <div className="col">
                              <FormControlLabel
                                className="w-100"
                                style={{ marginLeft: 0 }}
                                control={
                                  <FormikSwitch
                                    name="addAgent.localPresence"
                                    formik={formik}
                                    color="primary"
                                    type="checkbox"
                                    disabled={
                                      formik.values.addAgent.billingTier.toString() !==
                                      '1'
                                    }
                                  />
                                }
                                label="Connection Boost &nbsp;&nbsp;"
                              />
                            </div>
                          </div>
                        </LabelledOutline>
                      </div>
                    </form>
                  </FormGroup>
                </TabPanel>
                <TabPanel value="1">
                  <AddNumberModalTab
                    formik={formik}
                    // handleNumberSearch={handleNumberSearch}
                    numberToBuy={numberToBuy}
                    setNumberToBuy={setNumberToBuy}
                    setRate={setRate}
                  />
                </TabPanel>
                <TabPanel value="2">
                  <AddVMDropModalTab
                    formik={formik}
                    uploadedFiles={uploadedFiles}
                    setUploadedFiles={setUploadedFiles}
                    filenames={filenames}
                    setFilenames={setFilenames}
                    filepaths={filepaths}
                    setFilepaths={setFilepaths}
                    setVmDropRecording={setVmDropRecording}
                  />
                </TabPanel>
                <TabPanel value="3">
                  <AddSMSTemplateTab formik={formik} />
                </TabPanel>
              </WizardModal>
              <ProductChangesConfirmModal
                show={showProductChanges}
                handleClose={() => {
                  setShowProductChanges(false);
                }}
                userProductChanges={userProductChanges}
                onHandleSubmit={(val) => {
                  setProductChangesLoading(true);
                  submitForm(values, actions);
                  setShowProductChanges(false);
                }}
                enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
                currentUser={values?.addAgent ?? {}}
                newAgent={true}
                productChangesLoading={productChangesLoading}
              />
            </TabContext>
          );
        }}
      </Formik>
    </Paper>
  );
}
